import { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { calculateBoundingBox, parseGCode } from './utils.ts';
import GCodeRenderer from './Renderer.tsx';
import CenteredCamera from './CenteredCamera.tsx';
import { getPublicFileFromS3 } from '@/lib/s3.ts';

interface ModelViewerProps {
  modelURL: string;
  enableGrid?: boolean;
  backgroundColor?: string;
  gridColor?: string;
  modelColor?: string;
  boundingBoxColor?: string;
  dTextColor?: string;
}

function Viewer({
  modelURL,
  enableGrid = true,
  backgroundColor = 'lightgray',
  gridColor = '#696969',
  modelColor = 'green',
  boundingBoxColor = 'gray',
  dTextColor = 'black',
}: ModelViewerProps) {
  const [gcode, setGcode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGCode = async () => {
      try {
        setLoading(true);
        const response = await getPublicFileFromS3(modelURL);
        if (response.ok) {
          const gcodeText = await response.text();
          setGcode(gcodeText);
        } else {
          throw new Error('Failed to Load the model.');
        }
        setLoading(false);
      } catch {
        setLoading(false);
        setError('Failed to Load the model.');
      }
    };

    fetchGCode();
  }, [modelURL]);

  const { rapidMovePoints, linearMovePoints } = gcode
    ? parseGCode(gcode)
    : { rapidMovePoints: [], linearMovePoints: [] };

  const allPoints = [...rapidMovePoints, ...linearMovePoints];
  const boundingBox = calculateBoundingBox(allPoints);

  if (loading) {
    return <h1>Loading...</h1>;
  }

  if (error) {
    return <h1>{error}</h1>;
  }

  return (
    <Canvas
      camera={{ fov: 45 }}
      style={{ background: backgroundColor, borderRadius: '20px' }}
    >
      {boundingBox && <CenteredCamera boundingBox={boundingBox} />}

      <OrbitControls maxPolarAngle={Math.PI} />

      {enableGrid && (
        <Grid
          args={[150, 150]}
          position={[0, 0, 0]}
          rotation={[Math.PI / 2, 0, 0]}
          cellSize={1}
          sectionColor={gridColor}
          infiniteGrid
        />
      )}

      {gcode && (
        <GCodeRenderer
          gcode={gcode}
          modelColor={modelColor}
          boundingBoxColor={boundingBoxColor}
          dTextColor={dTextColor}
        />
      )}
    </Canvas>
  );
}

Viewer.defaultProps = {
  backgroundColor: 'lightgray',
  gridColor: '#696969',
  modelColor: 'green',
  boundingBoxColor: 'gray',
  enableGrid: true,
  dTextColor: 'black',
};

export default Viewer;
